export const PROMPT_UPLOAD_MAX_BYTES = 20 * 1024 * 1024;
export const PROMPT_UPLOAD_MAX_PAGES = 10;

export type PromptUploadKind = 'pdf' | 'image';

export const PROMPT_UNSUPPORTED_FILE_MESSAGE = 'Filtypen stöds inte. Ladda upp PDF, JPG eller PNG.';
export const PROMPT_FILE_TOO_LARGE_MESSAGE = 'Filen är för stor. Max storlek är 20 MB.';
export const PROMPT_SELECT_PAGE_MESSAGE = 'Välj minst en sida.';
export const PROMPT_MAX_PAGES_MESSAGE = 'Högst 10 sidor kan skickas i samma körning.';
export const PROMPT_PDF_READ_MESSAGE = 'Kunde inte läsa PDF-filen.';

export function detectClientUploadKind(fileName: string, mimeType?: string): PromptUploadKind {
  const name = String(fileName || '').trim().toLowerCase();
  const mime = String(mimeType || '').trim().toLowerCase();

  if (name.endsWith('.pdf') || mime === 'application/pdf') {
    return 'pdf';
  }

  const isJpeg = name.endsWith('.jpg')
    || name.endsWith('.jpeg')
    || mime === 'image/jpeg'
    || mime === 'image/jpg'
    || mime === 'image/pjpeg';
  const isPng = name.endsWith('.png') || mime === 'image/png' || mime === 'image/x-png';

  if (isJpeg || isPng) {
    return 'image';
  }

  throw new Error(PROMPT_UNSUPPORTED_FILE_MESSAGE);
}

export function assertClientUploadSize(byteLength: number): void {
  if (!Number.isFinite(byteLength) || byteLength <= 0) {
    throw new Error('Filen är tom.');
  }
  if (byteLength > PROMPT_UPLOAD_MAX_BYTES) {
    throw new Error(PROMPT_FILE_TOO_LARGE_MESSAGE);
  }
}

export type PromptPdfCropRegion = {
  pageNumber: number;
  xPct: number;
  yPct: number;
  widthPct: number;
  heightPct: number;
};

export const PROMPT_BOTTOM_TRIM_KEEP_TOP_PCT = 0.72;

function clampPct(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

export function sanitizePromptCropRegion(input: unknown): PromptPdfCropRegion | null {
  const raw = input as { pageNumber?: unknown; xPct?: unknown; yPct?: unknown; widthPct?: unknown; heightPct?: unknown };
  const pageNumber = Number(raw?.pageNumber);
  const xPct = clampPct(Number(raw?.xPct));
  const yPct = clampPct(Number(raw?.yPct));
  const widthPct = clampPct(Number(raw?.widthPct));
  const heightPct = clampPct(Number(raw?.heightPct));

  if (!Number.isInteger(pageNumber) || pageNumber < 1) return null;
  if (widthPct <= 0 || heightPct <= 0) return null;
  if (xPct >= 1 || yPct >= 1) return null;

  return {
    pageNumber,
    xPct,
    yPct,
    widthPct: Math.min(widthPct, 1 - xPct),
    heightPct: Math.min(heightPct, 1 - yPct),
  };
}

export function sanitizePromptCropRegions(raw: unknown, pageNumbers?: number[]): PromptPdfCropRegion[] {
  const list = Array.isArray(raw) ? raw : [];
  const allowed = pageNumbers ? new Set(pageNumbers) : null;
  const byPage = new Map<number, PromptPdfCropRegion>();

  for (const item of list) {
    const region = sanitizePromptCropRegion(item);
    if (!region) continue;
    if (allowed && !allowed.has(region.pageNumber)) continue;
    byPage.set(region.pageNumber, region);
  }

  return Array.from(byPage.values()).sort((left, right) => left.pageNumber - right.pageNumber);
}

export function createBottomTrimCropRegions(
  pageNumbers: number[],
  keepTopPct = PROMPT_BOTTOM_TRIM_KEEP_TOP_PCT,
): PromptPdfCropRegion[] {
  const heightPct = clampPct(keepTopPct);
  if (heightPct <= 0) return [];
  return pageNumbers
    .filter((pageNumber) => Number.isInteger(pageNumber) && pageNumber >= 1)
    .map((pageNumber) => ({
      pageNumber,
      xPct: 0,
      yPct: 0,
      widthPct: 1,
      heightPct,
    }));
}

export function formatPageChipLabel(pageNumbers: number[], cropped = false): string {
  const pages = pageNumbers.length === 1
    ? `Sida ${pageNumbers[0]}`
    : `Sidor ${pageNumbers.join(', ')}`;
  return cropped ? `${pages} · beskuren` : pages;
}

export function togglePageNumber(selected: number[], pageNumber: number): number[] {
  const next = new Set(selected);
  if (next.has(pageNumber)) {
    next.delete(pageNumber);
  } else {
    next.add(pageNumber);
  }
  return Array.from(next).sort((left, right) => left - right);
}

export function validateClientPageSelection(selected: number[], pageCount: number): string | null {
  if (!selected.length) {
    return PROMPT_SELECT_PAGE_MESSAGE;
  }
  if (selected.length > PROMPT_UPLOAD_MAX_PAGES) {
    return PROMPT_MAX_PAGES_MESSAGE;
  }
  if (selected.some((page) => page < 1 || page > pageCount)) {
    return 'Ogiltigt sidnummer.';
  }
  return null;
}

export async function getClientPdfPageCount(file: File): Promise<number> {
  const { getDocument } = await import('pdfjs-dist/webpack');
  const data = new Uint8Array(await file.arrayBuffer());
  const task = getDocument({ data });
  try {
    const pdf = await task.promise;
    const pageCount = pdf.numPages;
    await pdf.destroy();
    if (!pageCount) {
      throw new Error(PROMPT_PDF_READ_MESSAGE);
    }
    return pageCount;
  } catch (error: any) {
    if (error?.message === PROMPT_PDF_READ_MESSAGE) {
      throw error;
    }
    throw new Error(PROMPT_PDF_READ_MESSAGE);
  }
}
